/* Колонка списка голосов: вкладка «Озвучка» (#vp), правая панель (#svp) и «Другой голос» (#bcSetVp).
   Сам список — виджет: разметка кладётся в колонку целиком. Колонка должна быть узлом React,
   иначе быстрый поиск каркаса не перехватит innerHTML и выдача перестроится с потерей фокуса. */
import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { flushSync } from 'react-dom';
import type { VoiceCommit } from './voice-list';

/** Куда виджет отдаёт разметку. release снимает список */
type Bind = (commit: VoiceCommit) => () => void;

export function VoiceListBody({ id, bind }: { id: string; bind: Bind }): ReactNode {
  const pending = useRef<(() => void) | null>(null);
  const bindRef = useRef(bind);
  /* Первое подключение идёт из эффекта ниже: там flushSync не может вставить разметку (React уже в фазе коммита),
     и «после вставки» (прокрутка, индикатор вкладки, догрузка) выполнялось раньше, чем список появлялся в DOM */
  const binding = useRef(false);
  const shown = useRef('');
  const [html, setHtml] = useState('');
  useLayoutEffect(() => {
    const done = pending.current;
    pending.current = null;
    if (done) done();
    /* Полоса прокрутки сужает колонку на --sbw. Мерить после вставки списка: раньше узла ещё нет, и запас не ставится. */
    const el = document.getElementById(id);
    if (!el?.querySelector('#vscroll')) return;
    window.SteosUI.syncGutters('.cbody,.vscroll');
    const seg = el.querySelector('.vseg');
    if (seg) window.SteosUI.segInd(seg);
  });
  useLayoutEffect(() => {
    binding.current = true;
    try {
      return bindRef.current((markup, done) => {
        if (markup === shown.current) {
          pending.current = null;
          done();
          return;
        }
        shown.current = markup;
        pending.current = done;
        /* внутри эффекта: React вставит разметку сразу после него, а done вызовет эффект выше, уже по готовому DOM */
        if (binding.current) {
          setHtml(markup);
          return;
        }
        flushSync(() => setHtml(markup));
        if (!pending.current) return;
        const fn = pending.current;
        pending.current = null;
        fn();
      });
    } finally {
      binding.current = false;
    }
  }, [id]);
  return <div className="cbody fixed" id={id} dangerouslySetInnerHTML={{ __html: html }} />;
}
